/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser, PlatformLocation } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { genTinyGUID } from '@cudirect/shared-ui-library';
import { SSN_REGEX } from '@shared/constant/regex.const';
import { LenderService } from '@shared/service/lender/lender.service';
import { ValueLabelItem } from '@workflow/class/enum-item.class';
import { FieldInstance, FieldService } from '@workflow/class/field.service';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { ApiResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import { HostPrefillService } from '@workflow/ecp-workflow/dynamic-app/service/host-prefill.service';
import {
  EmployerAddress,
  EmploymentInfo,
  Identification,
  Identity,
  InstaTouchAddress,
  InstaTouchUserInformation,
  Pay,
  PersonalInfo
} from '@workflow/ecp-workflow/dynamic-app/service/instaTouch/insta-touch.types';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import {
  getPageIterationId,
  getValue,
  UtilitiesService,
  validatePageOnPrefill
} from '@workflow/ecp-workflow/dynamic-app/service/utilities.service';
import { BranchList } from '@workflow/ecp-workflow/dynamic-app/types/branchList.type';
import { PickList, PickListTypes } from '@workflow/ecp-workflow/dynamic-app/types/picklist';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { getStateInfo } from 'src/postapp/consts/region-states.const';
import { loanDetailsMockData, OLAUITemplates } from '../../types/constants';
import { InitializeAppConfigDataService } from '@app/service/initialize-app.config.service';
import { OaoPageHelper } from '@workflow/ecp-workflow/oao/helper/oao-page.helper';

const buildEnumItem = (value, label?, id?: number) => {
  return {
    id,
    label: label || value,
    value
  } as ValueLabelItem;
};

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OlaCustomFunctionsService {
  private logger = inject(SawDust);
  private readonly statefulService = inject(StatefulService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor(
    private readonly location: PlatformLocation,
    private readonly lenderService: LenderService,
    public readonly l360ApiService: L360Service,
    private readonly prefillService: HostPrefillService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly appInitService: InitializeAppConfigDataService,
    public utils: UtilitiesService,
    private readonly fieldService: FieldService,
    private readonly flowHelper: OaoPageHelper
  ) {}
  /**
   * get Lender name
   */
  public lenderName = (context: LogicFnContext): void => {
    const lenderName = this.lenderService.lenderData.lenderName;
    let newLabel = context.field.config.label;
    newLabel = newLabel.replace('{$}', lenderName);
    context.field.config.label = newLabel;
  };

  /**
   * Title for current working here field (Employment & Income)
   * @param context
   * @param employmentType
   */
  public currentlyWorkingTitle = (context: LogicFnContext, employmentType: string): void => {
    let newLabel = context.field.config.label;
    const employmentNewType = employmentType && employmentType.toLowerCase() === 'selfemployed1120' ? 'Self-employed' : employmentType;
    newLabel = newLabel.replace('{$}', employmentNewType.toLowerCase());
    context.field.config.label = newLabel;
  };

  /**
   * Title for loan type selection
   * @param context
   * @param title patch
   */
  public selectedLoanTitle = (context: LogicFnContext, titlePatch: string): void => {
    let newLabel = context.field.config.label;
    newLabel = newLabel.replace('{$}', titlePatch);
    context.field.config.label = newLabel;
  };

  public setWorkFlowType = (context: LogicFnContext, dynamicFlowType: string): void => {
    this.statefulService.wrap('mem:dynamic-flow-for').value = dynamicFlowType;
  };

  /**
   * update instaTouch prefill data for OLA flow
   */
  public updateBorrowerData = (context: LogicFnContext, isCoApp: boolean, protocol: string = 'api'): void => {
    const pageIterationId = context.field.pageIterationId;
    /* Getting stringified Instatouch response from field context and parsing it to object */
    const data = context.field.data.valueStringOnly;
    const alertmessageId = isCoApp ? 'ss:ola-coborrower-prefill-alert-#' : 'ss:ola-borrower-prefill-alert';
    if (!data) {
      this.statefulService.wrap(alertmessageId, null, pageIterationId).value = Info.warning;
      return;
    }
    let parsedData;
    try {
      // why and how did we end up here from calling personalinformation?
      parsedData = JSON.parse(data) as InstaTouchUserInformation;
    } catch (error) {
      this.logger.errorOnly(error);
      this.statefulService.wrap(alertmessageId, null, pageIterationId).value = Info.warning;
    }
    const root = `${protocol}:${isCoApp ? 'coBorrowers/#/' : ''}borrowerInfo`;

    if (!parsedData) {
      this.statefulService.wrap(alertmessageId, null, pageIterationId).value = Info.warning;
      return;
    }

    const autoFillOption = parsedData?.isManual ? 'Manual' : 'MobileNumber';
    this.statefulService.wrap(`${root}/autoFillOption`, undefined, pageIterationId).value = autoFillOption;
    this.statefulService.wrap(`${root}/isPrefilled`, undefined, pageIterationId).value = parsedData?.isManual ? false : true;
    if (isCoApp) {
      const coBorrowerRootPrefix = `${protocol}:coBorrowers/${pageIterationId}`;
      this.statefulService.wrap(`${coBorrowerRootPrefix}/borrowerInfo/sameAsPrimaryAddress`).value = false;
      this.statefulService.wrap(`${coBorrowerRootPrefix}/borrowerInfo/autoFillOption`).value = autoFillOption;
      this.statefulService.wrap(`${coBorrowerRootPrefix}/borrowerInfo/isPrefilled`).value = parsedData?.isManual ? false : true;
    }
    if (!parsedData?.isManual) {
      this.statefulService.wrap('mem:has-data-' + context.page.id, null, context.page?.pageIterationId).value = true;
    }
    this.statefulService.wrap(`${root}/autoFillStatus`, undefined, pageIterationId).value = true;
    if (parsedData.givenMobileNumber) {
      this.statefulService.wrap(`${root}/primaryPhoneNumber`, undefined, pageIterationId).value = parsedData.givenMobileNumber;
    }

    this.updateInstaTouchPersonalInfo(root, pageIterationId, protocol, isCoApp, parsedData?.personalInfo);
    this.updateInstaTouchEmploymentInfo(pageIterationId, protocol, isCoApp, parsedData?.employmentInfo);
    validatePageOnPrefill(this.statefulService, context, this.location, isCoApp ? 'ola-co-applicant-preview-#' : 'ola-personal-preview');
    this.statefulService.wrap(alertmessageId, null, pageIterationId).value = Info.success;
  };

  /** It's used to prefill the employment info from InstaTouch */
  private updateInstaTouchEmploymentInfo = (
    pageIterationId: number,
    protocol: string,
    isCoApp: boolean,
    employmentInfo: EmploymentInfo
  ): void => {
    if (!employmentInfo?.employers || employmentInfo?.employers?.length === 0) {
      return;
    }

    const emproot = `${protocol}:${isCoApp ? 'coBorrowers/' + pageIterationId + '/' : ''}employments`;
    let length = this.statefulService.arraySize(emproot);
    employmentInfo.employers.forEach((employment) => {
      if (length !== 1) {
        this.statefulService.arrayAdd(emproot);
        length++;
      }
      const index = length - 1;
      this.statefulService.wrap(`${emproot}/${index}/employmentType`).value = '1'; // Default Employed
      this.statefulService.wrap(`${emproot}/${index}/employerorBusinessName`).value = employment.employerName;
      if (employment.startDate && Date.parse(employment.startDate)) {
        const startDateArray = employment.startDate.split('/');
        this.statefulService.wrap(`${emproot}/${index}/startDate`).value = `${startDateArray[0]}/${startDateArray[2]}`;
      }
      this.statefulService.wrap(`${emproot}/${index}/positionOrTitle`).value = employment.jobTitle;

      this.updateInstaTouchEmployerPayDetails(emproot, index, employment?.pay);
      this.updateInstaTouchEmployerAddress(emproot, index, employment?.employerAddress);
    });
  };

  /** It's used to prefill the employer pay details from InstaTouch */
  private updateInstaTouchEmployerPayDetails = (emproot: string, index: number, pay: Pay): void => {
    if (!pay) {
      return;
    }

    const frequency = pay.payFrequency;
    let payFrequency = '';
    if (frequency === 'Yearly') {
      payFrequency = 'Annually';
    } else if (frequency === 'Monthly') {
      payFrequency = 'Monthly';
    } else if (frequency === 'BiWeekly') {
      payFrequency = 'Biweekly';
    } else if (frequency === 'Weekly') {
      payFrequency = 'Weekly';
    }
    if (payFrequency) {
      this.statefulService.wrap(`${emproot}/${index}/payFrequency`).value = payFrequency;
    }

    if (pay.annualIncome) {
      this.statefulService.wrap(`${emproot}/${index}/grossIncome`).value = pay.annualIncome;
    }
  };

  /** It's used to prefill the employer address info from InstaTouch */
  private updateInstaTouchEmployerAddress = (emproot: string, index: number, employerAddress: EmployerAddress): void => {
    if (!employerAddress) {
      return;
    }

    this.statefulService.wrap(`${emproot}/${index}/address/address1`).value = employerAddress.streetAddress1;
    this.statefulService.wrap(`${emproot}/${index}/address/city`).value = employerAddress.city;
    this.statefulService.wrap(`${emproot}/${index}/address/state`).value = employerAddress.state;
    this.statefulService.wrap(`${emproot}/${index}/address/zip`).value = employerAddress.zipCode;
  };

  /** It's used to prefill the personal info from InstaTouch */
  private updateInstaTouchPersonalInfo = (
    root: string,
    pageIterationId: number,
    protocol: string,
    isCoApp: boolean,
    personalInfo: PersonalInfo
  ): void => {
    if (!personalInfo) {
      return;
    }
    this.updateInstaTouchIdentification(root, pageIterationId, personalInfo?.identification);
    this.updateInstaTouchIdentity(root, pageIterationId, protocol, isCoApp, personalInfo?.identity);
  };

  /** It's used to set the flag values Identification from InstaTouch */
  private readonly updateInstaTouchIdentification = (root: string, pageIterationId: number, identification: Identification): void => {
    if (!identification) {
      return;
    }
    this.statefulService.wrap(`${root}/dateOfBirth`, undefined, pageIterationId).value = identification.dob;
    this.statefulService.wrap(`${root}/ssn`, undefined, pageIterationId).value = identification.ssn;

    if (identification.dob != null) {
      this.statefulService.wrap(`${root}/autoFillDateOfBirthValid`, undefined, pageIterationId).value = true;
    } else {
      this.statefulService.wrap(`${root}/autoFillDateOfBirthValid`, undefined, pageIterationId).value = false;
    }

    if (identification.ssn?.length === 9) {
      // Enable SSN field for user editing when the prefilled SSN fails validation
      // This allows users to correct invalid SSNs from InstaTouch data
      // Logic: enableAutoFillSSN = true when SSN is invalid, false when valid
      this.statefulService.wrap(`${root}/enableAutoFillSSN`, undefined, pageIterationId).value = !!identification.ssn.match(SSN_REGEX)
        ? false
        : true;
    }
  };

  /** It's used to set the flag values identity from InstaTouch */
  private readonly updateInstaTouchIdentity = (
    root: string,
    pageIterationId: number,
    protocol: string,
    isCoApp: boolean,
    identity: Identity
  ): void => {
    if (!identity) {
      return;
    }

    if (identity.contact) {
      if (isCoApp) {
        this.statefulService.wrap(`${root}/emailAddress`, undefined, pageIterationId).value = identity.contact.email;
      }
      this.statefulService.wrap(`${root}/phoneType`, undefined, pageIterationId).value = 'Mobile';
      this.statefulService.wrap(`${root}/emailAddress`, undefined, pageIterationId).value = identity.contact.email;
    }

    if (identity.name) {
      this.statefulService.wrap(`${root}/firstName`, undefined, pageIterationId).value = identity.name.firstName;
      this.statefulService.wrap(`${root}/lastName`, undefined, pageIterationId).value = identity.name.lastName;
    }
    if (identity.name?.firstName) {
      this.statefulService.wrap(`${root}/enableAutoFillFirstName`, undefined, pageIterationId).value = false;
    }

    if (identity.contact?.email) {
      this.statefulService.wrap(`${root}/enableAutoFillEmailAddress`, undefined, pageIterationId).value = false;
    }
    if (identity.contact?.mobile) {
      this.statefulService.wrap(`${root}/enableAutoFillMobileNumber`, undefined, pageIterationId).value = false;
    }

    this.updateInstaTouchAddress(pageIterationId, protocol, isCoApp, identity?.addresses);
  };

  public normalizeState(address: InstaTouchAddress): void {
    const oldState = address.state;
    address.state = getStateInfo(address.state) || address.state;
    if (address.state !== oldState) {
      this.logger.info('DataCleansing: State value updated', { from: oldState, to: address.state, component: 'OlaCustomFunctionsService' });
    }
  }

  /** It's used to prefill the borrower address from InstaTouch */
  private updateInstaTouchAddress = (pageIterationId: number, protocol: string, isCoApp: boolean, addresses: InstaTouchAddress[]): void => {
    if (!addresses || addresses?.length === 0) {
      return;
    }

    const addressroot = `${protocol}:${isCoApp ? 'coBorrowers/' + pageIterationId + '/' : ''}borrowerInfo/addresses`;
    let length = this.statefulService.arraySize(addressroot);
    addresses.forEach((address) => {
      if (length !== 1) {
        this.statefulService.arrayAdd(addressroot);
        length++;
      }

      const index = length - 1;

      if (address?.state && address?.state?.length !== 2) {
        this.normalizeState(address);
      }

      this.statefulService.wrap(`${addressroot}/${index}/address1`).value = address.streetAddress;
      this.statefulService.wrap(`${addressroot}/${index}/city`).value = address.city;
      this.statefulService.wrap(`${addressroot}/${index}/state`).value = address.state;
      this.statefulService.wrap(`${addressroot}/${index}/zip`).value = address.zipCode;
    });
  };

  /**
   * Build custom enum items for vehicle Year Dropdown
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public vehicleYearEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const years = loanDetailsMockData.map((t) => t.Year).sort();
    return Array.from(new Set(years)).map((year) => buildEnumItem(year));
  };

  /**
   * Build custom enum items for vehicle Make Dropdown
   */
  public vehicleMakeEnumBuilder = (context: LogicFnContext, parentYear: string): ValueLabelItem[] => {
    const selectedYear = context.field.payload[parentYear];
    if (!selectedYear) {
      // context.field.config.tooltip = 'Please specify a vehicle Year';
      return [];
    } else {
      // context.field.config.tooltip = '';
    }

    const makes = loanDetailsMockData
      .filter((t) => t.Year === Number(selectedYear))
      .map((t) => t.Make)
      .sort();
    return Array.from(new Set(makes)).map((make) => buildEnumItem(make));
  };

  public vehicleModelEnumBuilder = (context: LogicFnContext, parentMake: string): ValueLabelItem[] => {
    const selectedMake = context.field.payload[parentMake];
    if (!selectedMake) {
      // context.field.config.tooltip = 'Please specify a vehicle Make';
      return [];
    } else {
      // context.field.config.tooltip = '';
    }

    const models = loanDetailsMockData
      .filter((t) => t.Make === selectedMake)
      .map((t) => t.Model)
      .sort();
    return Array.from(new Set(models)).map((make) => buildEnumItem(make));
  };

  public loadLoanTerm = (context: LogicFnContext, parentFieldId: string, targetFieldId: string, targetDropdownFieldId: string): void => {
    const iterationID = getPageIterationId(context);
    const parentValue = this.statefulService.wrap(parentFieldId, undefined, iterationID).value;
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName', undefined, iterationID).value;
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName', undefined, iterationID).value;
    const targetField = this.fieldService.createField(targetFieldId, context.field.flowId, undefined, iterationID);

    const payLoad = targetField.config.payload;
    payLoad.min = Number(0);
    payLoad.max = Number(0);

    if (
      this.adminPortalFeaturesService.config.olaConfiguration &&
      this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups
    ) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      if (selectedLoanGroup) {
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];

        if (selectedLoanProduct) {
          const targetDropdownField = this.fieldService.createField(targetDropdownFieldId, context.field.flowId, undefined, iterationID);
          if (targetDropdownField.enum.length > 0) {
            targetDropdownField.enum.splice(0, targetDropdownField.enum.length);
          }
          selectedLoanProduct.requestAmountRanges.forEach((itemRange) => {
            if (parentValue >= itemRange.amountMin && parentValue <= itemRange.amountMax) {
              if (!selectedLoanProduct.isSelectedRange) {
                payLoad.min = itemRange.termMin;
                payLoad.max = itemRange.termMax;
              } else {
                const dropdownArray = itemRange.rangeValues;
                dropdownArray.forEach((element) => {
                  const enumvalue: any = {
                    enumId: `term_${element}`,
                    label: element,
                    value: element,
                    virtual: false
                  };
                  targetDropdownField.enum.push(enumvalue);
                });
              }
            }
          });
          this.selectValueFromDropdown(null, targetDropdownField);
        }
      }
    }
  };

  public validateloanamount = (
    context: LogicFnContext,
    requestAmountStorage: string,
    targetDropdownFieldId: string,
    selectedLoanProduct: any
  ): void => {
    const iterationID = getPageIterationId(context);
    if (selectedLoanProduct.isSelectedRange) {
      this.statefulService.wrap('ss:ola-terms-dropdown').value = true;
      this.statefulService.wrap('ss:ola-terms-input').value = false;
      const targetDropdownField = this.fieldService.createField(targetDropdownFieldId, context.field.flowId, undefined, iterationID);
      if (targetDropdownField.enum.length > 0) {
        targetDropdownField.enum.splice(0, targetDropdownField.enum.length);
      }
      const requestedLoanAmount = this.statefulService.wrap(requestAmountStorage, undefined, iterationID).value;
      selectedLoanProduct.requestAmountRanges.forEach((itemRange) => {
        if (requestedLoanAmount >= itemRange.amountMin && requestedLoanAmount <= itemRange.amountMax) {
          const dropdownArray = itemRange.rangeValues;
          dropdownArray.forEach((element) => {
            const enumvalue: any = {
              enumId: element,
              label: element,
              value: element,
              virtual: false
            };
            targetDropdownField.enum.push(enumvalue);
          });
        }
      });
    } else {
      this.statefulService.wrap('ss:ola-terms-dropdown').value = false;
      this.statefulService.wrap('ss:ola-terms-input').value = true;
    }
  };

  public setrequestedloanamountvalidation = (
    context: LogicFnContext,
    requestAmountStorage: string,
    targetDropdownFieldId: string
  ): void => {
    const iterationID = getPageIterationId(context);
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName', undefined, iterationID).value;
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName', undefined, iterationID).value;

    if (
      this.adminPortalFeaturesService.config.olaConfiguration &&
      this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups
    ) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      if (selectedLoanGroup) {
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];

        this.statefulService.wrap('ss:ola-terms-input').value = this.statefulService.wrap('ss:ola-terms-dropdown').value = false;

        if (selectedLoanProduct && !selectedLoanProduct.ignoreLoanAmountSettings && selectedLoanProduct.requestAmountRanges.length > 0) {
          this.validateloanamount(context, requestAmountStorage, targetDropdownFieldId, selectedLoanProduct);
        }

        if (selectedLoanProduct?.min && selectedLoanProduct?.max) {
          context.field.config.payload = { min: selectedLoanProduct.min, max: selectedLoanProduct.max };
          // eslint-disable-next-line @typescript-eslint/dot-notation
          context.field.data.config.validation['between'] = [selectedLoanProduct.min, selectedLoanProduct.max];
        }
      }
    }
  };

  public setLoanDetailsFromConfig = (context: LogicFnContext, targetDropdownFieldId: string): void => {
    const iterationID = getPageIterationId(context);
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName', undefined, iterationID).value;
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName', undefined, iterationID).value;

    if (
      this.adminPortalFeaturesService.config.olaConfiguration &&
      this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups
    ) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      if (selectedLoanGroup) {
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => !selectedLoanGroup.isLoanGroupHasProduct || t.productName === loanType
        )[0];

        if (!selectedLoanProduct) {
          return;
        }

        const targetDropdownField = this.fieldService.createField(targetDropdownFieldId, context.field.flowId, undefined, iterationID);
        if (targetDropdownField.enum.length > 0) {
          targetDropdownField.enum.splice(0, targetDropdownField.enum.length);
        }

        if (selectedLoanProduct) {
          let productDropdownArray: any[] = null;
          let uiTemplateName = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;
          uiTemplateName = uiTemplateName ? uiTemplateName.toLowerCase() : '';

          if (uiTemplateName === OLAUITemplates.AutoPurchaseTemplate || uiTemplateName === OLAUITemplates.AutoRefinanceTemplate) {
            productDropdownArray = selectedLoanProduct.olaLoanDetail.transactionTypes;
            // eslint-disable-next-line eqeqeq
          } else if (uiTemplateName == OLAUITemplates.PersonalTemplate || uiTemplateName == OLAUITemplates.PersonalLineOfCreditTemplate) {
            productDropdownArray = selectedLoanProduct.olaLoanDetail.loanPurposes;
          }
          if (uiTemplateName === OLAUITemplates.CreditCardTemplate) {
            if (targetDropdownFieldId === 'ola-credit-card-type') {
              productDropdownArray = selectedLoanProduct.olaLoanDetail.cardTypes;
            } else {
              productDropdownArray = selectedLoanProduct.olaLoanDetail.requestTypes;
            }
          }
          if (productDropdownArray) {
            productDropdownArray.forEach((element, index) => {
              const enumvalue: any = {
                enumId: index,
                label: element.displayName,
                value: element.displayName,
                virtual: false
              };
              targetDropdownField.enum.push(enumvalue);
            });
          }
        }
        this.selectValueFromDropdown(null, targetDropdownField);
      }
    }
  };

  /**
   * Load OAO Membership Names
   * @param targetFieldId - ID of Enum
   */
  public loadMembership = (_context: LogicFnContext, targetFieldId: string): void => {
    const targetField = this.fieldService.createField(targetFieldId, _context.field.flowId);

    // Only call the API when the enum is not loaded. Once enum loaded here, then it will not make api call
    if (targetField?.enum && targetField.enum.length === 0) {
      this.statefulService.wrap('mem:show-card-spinner').value = true;
      this.l360ApiService.getMemberships().subscribe(
        (response: any) => {
          if (response?.data?.memberships) {
            const memberships = response.data.memberships.filter(
              (membership) =>
                membership.membershipTypeName.toString().toLowerCase() === 'personal' ||
                membership.membershipTypeName.toString().toLowerCase() === 'business'
            );

            this.statefulService.wrap('ola:olaApplicationInformation/membership/organizationID').value = response.data.organizationID
              ? response.data.organizationID
              : 0;
            let enumvalue;
            memberships.forEach((element) => {
              enumvalue = {
                enumId: element.membershipTypeName,
                isYouth: element.isYouth,
                label: element.membershipName,
                value: element.membershipID,
                virtual: false
              };
              targetField.enum.push(enumvalue);
            });
            // targetField.enum. = enumValue;
            memberships.sort((a, b) => a.membershipName.localeCompare(b.membershipName));
            targetField.enum.sort((a, b) => a.label.localeCompare(b.label));
          }
          this.statefulService.wrap('mem:show-card-spinner').value = false;
        },
        () => {
          this.statefulService.wrap('mem:show-card-spinner').value = false;
        }
      );
    }
    /** Set value in memory to stored value in API */
    this.statefulService.wrap('mem:olaApplicationInformation/membership/membershipID').value = _context.field.data.valueString;
  };

  /**
   * Set OtherIncome add link label
   */
  public getOtherIncomeLinkLabel = (_context: LogicFnContext): void => {
    if (_context.field.iterationId > 0) {
      _context.field.config.label = 'Additional income';
    } else {
      _context.field.config.label = 'Add more income';
    }
  };

  /**
   * Select dropdown value on refresh
   */
  public selectValueFromDropdown = (_context: LogicFnContext, customField?: FieldInstance): void => {
    const field: FieldInstance = _context ? _context.field : customField;
    const value = this.statefulService.wrap(field.data.id).valueString;
    const dropDownList = document.getElementById(field.id) as HTMLSelectElement;
    if (dropDownList) {
      setTimeout(() => {
        dropDownList.value = value;
      }, 0);
    }
  };

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.logger.logEvent(eventName, eventData);
  }

  public onLoadOtherIncome = (_context: LogicFnContext): void => {
    if (_context.field.enum && _context.field.enum.length > 0) {
      return;
    }

    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    const logSrc = 'workflow';
    this.logEvent('ecp.ui.picklist', {
      eventState: 'start',
      correlationId: eventPairId,
      source: logSrc,
      keys: 'OtherIncomeSource'
    });

    const picklistTypes = this.l360ApiService.getPickListTypes('OtherIncomeSource');
    if (picklistTypes?.length > 0) {
      const dropdownValues = picklistTypes[0];
      if (!dropdownValues.pickListItems) {
        return;
      }

      let enumvalue;
      const picklists = dropdownValues.pickListItems.map((t) => t.name).sort();
      picklists.forEach((element) => {
        enumvalue = {
          enumId: element,
          label: element,
          value: element,
          virtual: false
        };
        _context.field.enum.push(enumvalue);
      });

      this.logEvent('ecp.ui.picklist', {
        eventState: 'end',
        correlationId: eventPairId,
        source: logSrc,
        keys: 'OtherIncomeSource'
      });
    }
  };

  /**
   * Asynchronously builds and returns a pick list of ValueLabelItem objects based on the given parameters.
   * If the payload is already loading or has dropdown lists, it processes those lists.
   * Otherwise, it retrieves pick list items from an API and processes the response.
   *
   * @param _context - The context of the logical function being executed.
   * @param pickListType - The type of the pick list to generate.
   * @param takeActualId - An identifier used in the processing of pick list response.
   * @param excludedOptions - Options to be excluded from the pick list.
   * @param includedOption - Options to be included in the pick list.
   * @param witnessType - Witness type parameter used in condition checks.
   * @param loanPurposeSubkey - Subkey used in retrieving pick list items.
   * @returns An array of ValueLabelItem objects representing the pick list.
   */
  public PickListEnumBuilder = (
    _context: LogicFnContext,
    pickListType: string,
    takeActualId: string,
    excludedOptions: string,
    includedOption: string,
    witnessType: string,
    loanPurposeSubkey: string
  ): ValueLabelItem[] => {
    // Extract field from the given context
    const field = _context.field;

    // this block can be removed in 5.3.1 or 5.4 - so long as 5.3.1 is at least 2 days after 5.3.0
    if (!field.config.payload) {
      field.config.payload = {};
      // fallback for an old workflow (won't need once cache is cleared)
      if (field.config.enum === 'ola-loan-card-types') {
        field.config.payload = {
          pickListType: 'CardTypeEditableControl',
          takeActualId: false, // we have to store string until the workflow is updated
          configPropertyName: 'cardType'
        };
      }
    }
    // If the field payload is loading or has existing dropdown lists, parse them
    if (field.config.payload.isLoading || (field.config.payload.dropdownLists && (field.config.payload.dropdownLists as any).length > 0)) {
      // Parse the dropdown lists from the payload or initialize an empty array
      let dropdownValues = field.config.payload.dropdownLists
        ? (JSON.parse(field.config.payload.dropdownLists as string) as ValueLabelItem[])
        : [];

      // If dropdownValues is not empty and the field payload includes a witnessType and an includedOption,
      // call includeValueAtFirst to modify the order of dropdownValues
      if (dropdownValues.length > 0 && _context.field.payload[witnessType] && _context.field.payload[includedOption]) {
        dropdownValues = this.includeValueAtFirst(_context, includedOption, witnessType, dropdownValues);
      }

      // TODO: DO WE NEED TO LOG TO UEM HERE? If we do, we see a million track events fire based on this logic. See 222952
      // Commenting this out, until we investigate and resolve within 222952
      // this.logPicklistTiming(eventPairId, logSrc, 'end', selectedpickListType);

      // Return the dropdownValues
      return dropdownValues;
    }

    // Extract the selected picklist type and loan purpose subkey from the field payload
    const selectedpickListType = String(field.payload[pickListType]);
    const selectedLoanPurposeSubkey = String(field.payload[loanPurposeSubkey]);

    // Generate an eventPairId for logging, and specify the log source as 'workflow'
    // NOTE: See comments above - we may need to start this earlier
    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    const logSrc = 'workflow';
    // Log the start time for loading picklist
    this.logPicklistTiming(eventPairId, logSrc, 'start', selectedpickListType);

    // Set the loading state to true since we are going to load new data
    field.config.payload.isLoading = true;

    /**
     * @remarks this.utils.hasValue(selectedLoanPurposeSubkey)
     * If hasValue(selectedLoanPurposeSubkey) it means we to get these picklists dynamically based on the user selected loan product.
     * Right now this is only for loan purpose picklist.
     * Said another way, we can't load the picklists on app init, because we need user data / user selections to pull them.
     * TODO: If we see that we are never using the subkey for other picklists, we should consider refactoring this such that
     * loanproduct/purpose gets it's own service method, and "general" picklists will always come from Angular memory (no conditional)
     */
    if (!this.utils.hasValue(selectedLoanPurposeSubkey)) {
      const picklistTypes = this.l360ApiService.getPickListTypes(selectedpickListType);

      // TODO: We need to look at refactoring this for clarity... it's a little crazy right now.
      if (picklistTypes?.length > 0) {
        // Process response
        return this.processPicklistResponse(
          picklistTypes,
          _context,
          takeActualId,
          excludedOptions,
          includedOption,
          witnessType,
          eventPairId,
          logSrc,
          selectedpickListType
        );
      }
    } else {
      const pickList = this.l360ApiService.getPickListTypes(selectedpickListType, selectedLoanPurposeSubkey);
      if (selectedpickListType === 'LoanPurpose' && pickList.length > 0) {
        // Process response
        return this.processPicklistResponse(
          pickList,
          _context,
          takeActualId,
          excludedOptions,
          includedOption,
          witnessType,
          eventPairId,
          logSrc,
          selectedpickListType
        );
      } else {
        // Request picklist items from the API
        // warning this is deferred so it breaks the return object here :(
        this.getAPIPickList(
          _context,
          takeActualId,
          excludedOptions,
          includedOption,
          witnessType,
          selectedpickListType,
          selectedLoanPurposeSubkey,
          eventPairId,
          logSrc,
          field
        );
      }
    }

    // Return an empty array if the function execution reaches this point
    // TODO - This might be a potential issue because the list above is being populated asynchronously... See 216140
    return [];
  };

  /**
   * Get Picklist response from API
   *
   * @param _context - The context of the logical function being executed
   * @param takeActualId - An identifier used for selecting between item.id and item.value in the result
   * @param excludedOptions - Options to exclude from the pick list
   * @param includedOption - Options to include in the pick list
   * @param witnessType - Witness type parameter used for condition checks
   * @param selectedpickListType - Selected Picklist Type from the Field
   * @param selectedLoanPurposeSubkey - Selected Loan Purpose Subkey from the Field
   * @param eventPairId - This is used for logging
   * @param logSrc - This is used to specify the log source as 'workflow'
   * @param field - The extracted field from the current context
   * @returns void
   */
  private getAPIPickList(
    _context: LogicFnContext,
    takeActualId: string,
    excludedOptions: string,
    includedOption: string,
    witnessType: string,
    selectedpickListType: string,
    selectedLoanPurposeSubkey: string,
    eventPairId: string,
    logSrc: string,
    field: FieldInstance
  ): void {
    this.l360ApiService.getPickListItems(selectedpickListType, selectedLoanPurposeSubkey).subscribe((data: ApiResult<PickListTypes>) => {
      // If the API call is successful, process the response to create picklist
      if (data.isSuccess) {
        const loanPurposePickListData = data?.data;
        if (loanPurposePickListData && selectedLoanPurposeSubkey) {
          loanPurposePickListData.picklistTypes.forEach((t: PickList) => {
            t.loanPurposeSubkey = selectedLoanPurposeSubkey;
          });
        }

        // set LoanPurposePickListData
        this.l360ApiService.LoanPurposePickListData = data?.data;
        // Process response
        return this.processPicklistResponse(
          data.data.picklistTypes,
          _context,
          takeActualId,
          excludedOptions,
          includedOption,
          witnessType,
          eventPairId,
          logSrc,
          selectedpickListType
        );
      }

      // If API call fails, set isLoading to false
      field.config.payload.isLoading = false;
    });
  }

  /**
   * Processes a picklist response, filters and sorts items based on context and other parameters, then returns a list of processed items
   *
   * @param data - The response data from the picklist API
   * @param _context - The context of the logical function being executed
   * @param takeActualId - An identifier used for selecting between item.id and item.value in the result
   * @param excludedOptions - Options to exclude from the pick list
   * @param includedOption - Options to include in the pick list
   * @param witnessType - Witness type parameter used for condition checks
   * @returns An array of ValueLabelItem objects that represent the processed picklist items
   */
  private processPicklistResponse(
    picklistTypes: PickList[],
    _context: LogicFnContext,
    takeActualId: string,
    excludedOptions: string,
    includedOption: string,
    witnessType: string,
    eventPairId,
    logSrc,
    selectedpickListType
  ): ValueLabelItem[] {
    // Extract dropdown values from the picklistTypes
    const dropdownValues = picklistTypes[0];

    // If dropdownValues.pickListItems is undefined or invalid id, return early
    if (!dropdownValues.pickListItems || +dropdownValues.id <= 0) return;

    // Assign pickListItems to a variable for further processing
    let pickListItems = dropdownValues.pickListItems;

    // If there are any excluded options, remove them from the pick list items
    if (_context.field.payload[excludedOptions] && (_context.field.payload[excludedOptions] as any).length > 0) {
      const excludedOptionsArray = JSON.parse(JSON.stringify(_context.field.payload[excludedOptions]).toLowerCase()) as string[];
      pickListItems = pickListItems.filter((t) => !excludedOptionsArray.includes(t.value.toLowerCase()));
    }

    // Sort the picklist items
    const picklists = this.sortPickListItems(pickListItems);

    // Reset the loading state
    _context.field.config.payload.isLoading = false;

    // Determine whether to take actual id or value
    const result = _context.field.payload[takeActualId] === undefined || _context.field.payload[takeActualId] ? true : false;

    // Build the list of ValueLabelItems, removing duplicates and mapping items to Enum items
    let list = Array.from(new Set(picklists)).map((item) => buildEnumItem(result ? item.id : item.value, item.value));

    // Assign the list back to the dropdown lists in the field payload
    _context.field.config.payload.dropdownLists = JSON.stringify(list);

    // If there are any included options, and the list is not empty, ensure these are added at the start
    if (list && list.length > 0 && _context.field.payload[witnessType] && _context.field.payload[includedOption]) {
      list = this.includeValueAtFirst(_context, includedOption, witnessType, list);
    }

    // Log the end time for loading picklist - don't include this in ppr below, because too many method params as it is
    this.logPicklistTiming(eventPairId, logSrc, 'end', selectedpickListType);

    // Return the processed list
    return list;
  }

  /**
   * Sorts an array of PickList items in ascending order based on their 'value' property.
   * This method performs an in-place sort on the array.
   *
   * @param pickListItems - An array of PickList items to be sorted.
   * @returns - The sorted array of PickList items.
   */
  private sortPickListItems = (pickListItems: PickList[]): PickList[] => {
    return pickListItems.sort((n1, n2) => {
      if (n1.value > n2.value) {
        return 1;
      }

      if (n1.value < n2.value) {
        return -1;
      }

      return 0;
    });
  };

  /**
   * Logs the timing of events related to the PickList process using the Application Insights service.
   *
   * @private
   * @param id - The correlation identifier for the event. This is used to track the associated process across multiple steps.
   * @param src - The source from which the event is triggered. This is used to identify the origin of the event.
   * @param timing - 'start' is used when the process begins, and 'end' is used when the process completes.
   * @returns void
   */
  private logPicklistTiming(id: string, src: string, timing: 'start' | 'end', keys: string): void {
    this.logEvent('ecp.ui.picklist', {
      eventState: timing,
      correlationId: id,
      source: src,
      keys
    });
  }

  private includeValueAtFirst = (
    _context: LogicFnContext,
    includedOption: string,
    witnessType: string,
    items: ValueLabelItem[]
  ): ValueLabelItem[] => {
    if (_context.field.payload[witnessType]) {
      const personIsA = _context.field.payload[witnessType]?.toString();
      if (personIsA?.toLowerCase() === 'jointcredit' || !personIsA) {
        items.unshift({ label: _context.field.payload[includedOption].toString(), value: _context.field.payload[includedOption] });
      }
    }
    return items;
  };

  private closingBranchEnumBuilder = (_context: LogicFnContext): ValueLabelItem[] => {
    const field = _context.field;

    if (!field.config.payload) {
      field.config.payload = {};
    }

    if (field.config.payload.isLoading || (field.config.payload.dropdownLists && (field.config.payload.dropdownLists as any).length > 0)) {
      const dropdownValues = field.config.payload.dropdownLists
        ? (JSON.parse(field.config.payload.dropdownLists as string) as ValueLabelItem[])
        : [];
      return dropdownValues;
    }
    field.config.payload.isLoading = true;
    this.l360ApiService.getBranchList().subscribe((data: ApiResult<BranchList[]>) => {
      if (data.isSuccess) {
        const branches = data.data;
        if (!branches) {
          return;
        }

        const picklists = branches.sort((n1, n2) => {
          if (n1.name > n2.name) {
            return 1;
          }

          if (n1.name < n2.name) {
            return -1;
          }

          return 0;
        });
        field.config.payload.isLoading = false;
        const list = Array.from(new Set(picklists)).map((item) => buildEnumItem(item.lenderDepartmentGuid, item.name));
        field.config.payload.dropdownLists = JSON.stringify(list);
      }
      field.config.payload.isLoading = false;
    });
    return [];
  };

  private getLoanGroup(): any {
    const selectedLoanGroupName = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    if (
      selectedLoanGroupName &&
      this.adminPortalFeaturesService.config &&
      this.adminPortalFeaturesService.config.olaConfiguration &&
      this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups
    ) {
      const loanGroup = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups.filter(
        // eslint-disable-next-line eqeqeq
        (t) => t.loanGroup.toLowerCase() == selectedLoanGroupName.toLowerCase()
      );
      return loanGroup && loanGroup.length > 0 ? loanGroup[0] : [];
    }

    return undefined;
  }

  /**
   * Waits for the co-borrower information to be fully initialized before proceeding.
   * Because there is a chance that the inProgress flag to be set to true long after
   * the has co-borrower flag is updated and saved, we have to rely on a fallback interval.
   * This can result in an unexpected experience in rare cases, but generally seems to resolve
   * consistently. Most cases I have seen result in immediate navigation, with a few
   * on change triggers, and in the current design, I have not seen it hit the interval.
   * @param context The function scope for the button click event
   */
  public waitForCoBorrower = (context: LogicFnContext): void => {
    const inProgress = this.statefulService.wrap('mem:ola-add-or-remove-co-applicant-inprogress');
    const expectedStatePath = 'mem:ola-add-or-remove-co-applicant-expected';
    const expectedState = this.statefulService.wrap(expectedStatePath);
    // if we don't have an expected state, we can't wait for it
    // in this case expected state value is undefined, so we just navigate to the next page
    if (expectedState.valueString === undefined || expectedState.valueString === null) {
      context.defaultNavigationFn();
      return;
    }

    // if we are in progress, attempt to wait for the co-borrower toggle to be saved
    // because of how stateful value works, we can't rely on the fact that the value "is"
    // actually set already - everything else relies on the database being updated. Fustrating.
    const coAppRef = this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower');

    if (inProgress.value === true || inProgress.value === 'true' || coAppRef.valueString !== expectedState.valueString) {
      const startTime = new Date();
      let hasRun = false;
      const instance = setInterval(() => {
        if (!hasRun) {
          if (coAppRef.valueString !== expectedState.valueString || inProgress.value === true || inProgress.value === 'true') {
            // we still don't match our expected state - let's keep waiting
            // note: in the interval we have to wait for in process to clear - unlike in the onChange event
            // this is frustrating but in some cases, it can be in progress and still hold the old value
            // this could take a while :(
            return;
          }
          const time = new Date().getTime() - startTime.getTime();
          hasRun = true;
          context.defaultNavigationFn();
          this.logEvent('ecp.ui.coBorrower.waitForCoBorrower', {
            waitTime: time,
            kind: expectedState.valueString === 'true' ? 'add' : 'remove'
          });
        }
        clearInterval(instance);
        coAppRef.unsubscribe(waiter);
      }, 500);
      const waiter = () => {
        if (!hasRun) {
          if (coAppRef.valueString !== expectedState.valueString) {
            // we still don't match our expected state - let's keep waiting in the interval
            return;
          }
          clearInterval(instance);
          hasRun = true;
          context.defaultNavigationFn();
          const time = new Date().getTime() - startTime.getTime();
          this.logEvent('ecp.ui.coBorrower.waitForCoBorrower', {
            waitTime: time,
            kind: expectedState.valueString === 'true' ? 'add' : 'remove'
          });
        }
        coAppRef.unsubscribe(waiter);
      };
      coAppRef.onChange(waiter);
    } else {
      context.defaultNavigationFn();
    }
  };

  public setCoapplicantAsJoint = (context: LogicFnContext) => {
    const loanProduct = this.getLoanProduct();
    const fieldValue = getValue(context);
    // eslint-disable-next-line eqeqeq
    if (fieldValue == true && !loanProduct?.allowCoSigner) {
      this.statefulService.wrap('ola:coBorrowers/0/borrowerInfo/creditType').value = 'JointCredit';
    }
  };

  private getLoanProduct(): any {
    const selectedLoanTypeName = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    const loanGroup = this.getLoanGroup();
    if (loanGroup && loanGroup.loanProducts) {
      const loanProduct = loanGroup.loanProducts.filter(
        (t) =>
          // eslint-disable-next-line eqeqeq
          !loanGroup.isLoanGroupHasProduct || (selectedLoanTypeName && t.productName.toLowerCase() == selectedLoanTypeName.toLowerCase())
      );
      return loanProduct && loanProduct.length > 0 ? loanProduct[0] : [];
    }
    return undefined;
  }

  private getLoanDetailDropdownList = (propertyName): ValueLabelItem[] => {
    const loanProduct = this.getLoanProduct();
    if (
      !loanProduct ||
      !loanProduct.olaLoanDetail ||
      !loanProduct.olaLoanDetail[propertyName] ||
      !loanProduct.olaLoanDetail[propertyName].pickList
    ) {
      return [];
    }
    const pickList = loanProduct.olaLoanDetail[propertyName].pickList;
    if (pickList.length > 0) {
      const list = Array.from(new Set(pickList)).map((item: any) => buildEnumItem(item.value, item.label, item.id));
      return list;
    }
    return [];
  };

  public LoanPurposeTypeEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const lenderLoanProductName = this.getLoanProduct().lenderLoanProduct;
    if (context.field.config.payload.loanPurposeSubkey !== lenderLoanProductName) {
      context.field.config.payload.dropdownLists = null;
      context.field.config.payload.loanPurposeSubkey = lenderLoanProductName;
    }

    // eslint-disable-next-line @typescript-eslint/dot-notation
    if (this.l360ApiService.picklistLoading['LoanPurpose']) {
      return [];
    }

    return this.PicklistConfigAndL360EnumBuilder(context);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public VehicleConditionEnumBuilder = (_context: LogicFnContext): ValueLabelItem[] => {
    return this.getLoanDetailDropdownList('vehicleCondition');
  };

  /**
   * Get values from Config and L360 picklist
   */
  public PicklistConfigAndL360EnumBuilder = (_context: LogicFnContext): ValueLabelItem[] => {
    let commonValues: ValueLabelItem[] = [];
    // eslint-disable-next-line @typescript-eslint/dot-notation
    const configValues = this.getLoanDetailDropdownList(_context.field.payload['configPropertyName']);
    const takeActualId = _context.field.payload.takeActualId;
    const L360Values = this.PickListEnumBuilder(
      _context,
      'pickListType',
      'takeActualId',
      'excludedOptions',
      'includedOption',
      'witnessType',
      'loanPurposeSubkey'
    );
    // If config is empty return all L360 items
    if (configValues.length === 0) {
      return L360Values;
    }
    // If L360 items are empty return empty list
    else if (L360Values.length === 0) {
      return [];
    }
    // Find the common values between config and L360 and return
    else {
      if (!!takeActualId) {
        commonValues = configValues
          .filter((item) => L360Values.some(({ value }) => +item.id === +value))
          .map((t) => {
            return {
              label: t.label,
              value: t.id,
              id: t.id
            };
          });
      } else {
        commonValues = configValues.filter((item) => L360Values.some(({ value }) => item.value === value));
      }

      // If there are no common values return L360 data
      if (commonValues.length < 1) {
        return L360Values;
      }
      return commonValues;
    }
  };

  /**
   * This will get the card type enum list. Uses the same
   * logic as the L360 picklist, but isn't exclusive filtering
   * to match previous logic.
   */
  public CardTypeEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    let commonValues: ValueLabelItem[] = [];
    const configValues = this.getLoanDetailDropdownList(context.field.payload.configPropertyName);
    const takeActualId = context.field.payload.takeActualId;
    const L360Values = this.PickListEnumBuilder(
      context,
      'pickListType',
      'takeActualId',
      'excludedOptions',
      'includedOption',
      'witnessType',
      'loanPurposeSubkey'
    );
    // If config is empty return all L360 items
    if (configValues.length === 0) {
      return L360Values;
    }
    // Find the common values between config and L360 and return
    else {
      if (!!takeActualId) {
        commonValues = configValues.map((t) => {
          return {
            label: t.label,
            value: t.id,
            id: t.id
          };
        });
      } else {
        commonValues = configValues;
      }

      // If there are no common values return L360 data
      if (commonValues.length < 1) {
        return L360Values;
      }
      return commonValues;
    }
  };

  /**
   * This function handles mapping the id value to the display value on the bundle
   * this is needed to match the previous functionality that matches on string names.
   * Someday we could fully migrate to just ids, but that would require additional work
   * after all existing valid bundles are using this logic.
   */
  public mappIdToValueProp = (context: LogicFnContext, propFieldPath: string): void => {
    const idValue = this.statefulService.wrap(context.field.config.storageId).value;
    if (idValue) {
      let pickList: ValueLabelItem[];
      if (context.field.payload.configPropertyName === 'cardType') {
        pickList = this.CardTypeEnumBuilder(context);
      } else if (context.field.payload.configPropertyName === 'loanPurpose') {
        pickList = this.LoanPurposeTypeEnumBuilder(context);
      } else {
        pickList = this.PicklistConfigAndL360EnumBuilder(context);
      }
      const match = pickList.find((item) => item.id === idValue);
      if (match) {
        this.statefulService.wrap(propFieldPath).value = match.label;
      }
    }
  };

  /** This logic runs once as a forward migration to ensure we can properly show a picklist selection */
  public ensureMapPicklistValue = (context: LogicFnContext, propFieldPath: string): void => {
    const idValue = this.statefulService.wrap(context.field.config.storageId).value;
    if (!idValue) {
      const labelValue = this.statefulService.wrap(propFieldPath).value;
      // nothing selected even in legacy dropdown - we can skip
      if (!labelValue) return;
      // we need to map the id from the bundle string value
      let pickList: ValueLabelItem[];
      if (context.field.payload.configPropertyName === 'cardType') {
        pickList = this.CardTypeEnumBuilder(context);
      } else if (context.field.payload.configPropertyName === 'loanPurpose') {
        pickList = this.LoanPurposeTypeEnumBuilder(context);
      } else {
        pickList = this.PicklistConfigAndL360EnumBuilder(context);
      }
      const match = pickList.find((item) => item.label === labelValue);
      if (match) {
        this.statefulService.wrap(context.field.config.storageId).value = match.id;
      }
    }
  };

  /**
   * Select dropdown value on refresh
   */
  public setDropdownValueFromBundle = (_context: LogicFnContext, customField?: FieldInstance): void => {
    const field: FieldInstance = _context ? _context.field : customField;
    const value = this.statefulService.wrap(field.data.id).valueString;

    // shortcut - when we can validate against the app init data, just do that instead of waiting for loaded picklist
    if (this.flowHelper.flow?.config?.streamlined && this.canValidateOffAppData(field, value)) {
      const dropDownList = document.getElementById(field.id) as HTMLSelectElement | null;
      if (dropDownList) {
        setTimeout(() => {
          dropDownList.value = value;
        }, 500);
      }
      return;
    }

    const dropdownValues = field.config?.payload?.dropdownLists
      ? (JSON.parse(field.config.payload.dropdownLists as string) as ValueLabelItem[])
      : [];
    const valueExists = dropdownValues.some((f) => f.value.toString() === value);

    if (!valueExists) {
      field.data.value = null;
    }

    if (!this.isBrowser || field.config?.payload?.isLoading) {
      return;
    }

    const dropDownList = document.getElementById(field.id) as HTMLSelectElement | null;
    if (!dropDownList) {
      return;
    }

    if (valueExists) {
      setTimeout(() => {
        dropDownList.value = value;
      }, 1000);
      return;
    }

    dropDownList.value = null;
  };

  /**
   * If the picklist is defined in our init call, we can use this to validate setting the value
   * instead of waiting for the picklist to be loaded separately - separate effort can be done
   * to eliminate the separate call to load picklists - because it's not needed.
   *
   * Current support is for housing type, id type, and employment status (common prefill picklists)
   */
  private canValidateOffAppData(field: FieldInstance, value: string): boolean {
    if (field.config.enum) {
      // check if we can map the enum - check if the value is in the mapped enum
      let enumValues = [];
      switch (field.config.enum) {
        case 'oao-employment-status':
        case 'ola-employment-status':
          enumValues = this.appInitService.appConfigData.picklist.picklistTypes.find(p => p.name === 'Employment Status')?.pickListItems || [];
          break;
        case 'oao-id-types':
        case 'ola-id-types':
          enumValues = this.appInitService.appConfigData.picklist.picklistTypes.find(p => p.name === 'Identification Type')?.pickListItems || [];
          break;
        case 'oao-housing-types':
        case 'ola-property-arrangement-type':
          enumValues = this.appInitService.appConfigData.picklist.picklistTypes.find(p => p.name === 'Housing Type')?.pickListItems || [];
          break;
      }
      if (enumValues.length > 0) {
        const match = enumValues.find((item) => item.value.toString() === value);
        if (match) {
          return true;
        } else {
          field.data.value = null;
        }
      }
    }
    return false;
  }

  public getGrossIncomeLabel = (_context: LogicFnContext): string => {
    let label = _context.field.config.label;
    const storage = _context.field.config.storageId;
    let payFrequencyPath = storage.replace('grossIncome', 'payFrequency');
    if (payFrequencyPath && payFrequencyPath.indexOf('otherIncomes') > -1) {
      payFrequencyPath = storage.replace('grossIncome', 'incomeFrequency');
    }
    const payFrequency = this.statefulService.wrap(
      payFrequencyPath,
      _context.field.iterationId,
      _context.field.pageIterationId
    ).valueString;
    if (payFrequency) {
      label = `${payFrequency} ${label}`;
    }
    return label;
  };

  public tryOLALoadPrefillData = (context: LogicFnContext) => {
    const otuToken = this.statefulService.wrap('ola:hostPrefillToken').value as string;
    this.prefillService.flowId = 'ola';
    if (otuToken) {
      this.statefulService.wrap('mem:ola-prefill-inprogress').value = true;
      this.statefulService.wrap('ola:hostPrefillToken').value = null;
      const root = 'ola:borrowerInfo';
      const targetField = this.fieldService.createField('ola-dynamic-personal-info', context.page?.flowId);
      if (!targetField?.visible) {
        this.statefulService.wrap(`${root}/idType`, 0).value = '';
        this.statefulService.wrap(`${root}/idNumber`, 0).value = '';
        this.statefulService.wrap(`${root}/issuingCountry`, 0).value = '';
        this.statefulService.wrap(`${root}/issuingState`, 0).value = '';
        this.statefulService.wrap(`${root}/expirationDate`, 0).value = null;
        this.statefulService.wrap(`${root}/issuedDate`, 0).value = null;
      }
      this.statefulService.wrap('mem:ola-prefill-inprogress').value = false;
      const [previewCardId] = context.page.cards.filter((t) => t.config.customCardType === 'autofill-preview').map((t) => t.id);
      if (
        context.page.cards.filter((t) => t.visible && t.helper.flatVisibleFields.findIndex((f) => f.required && !f.valid) !== -1).length !==
        0
      ) {
        this.statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = true;
      } else {
        this.statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = false;
      }
    }
  };

  /**
   * Pre load Id type dropdown
   * @param context Logic Fcn
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public preLoadIdTypePicklists = (context: LogicFnContext) => {
    const otuToken = this.statefulService.wrap('ola:hostPrefillToken').value as string;
    if (otuToken) {
      // Extract Loan GroupId & Loan Product Id
      const loanGroupName = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
      const loanProductName: string = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
      let groupId;
      let productId;

      if (
        loanGroupName &&
        loanProductName &&
        this.adminPortalFeaturesService.config.olaConfiguration &&
        this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups
      ) {
        const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup.toLowerCase() === loanGroupName.toLowerCase())[0];
        groupId = selectedLoanGroup ? selectedLoanGroup.groupId : '';

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => !selectedLoanGroup.isLoanGroupHasProduct || t.productName === loanProductName
        )[0];
        productId = selectedLoanProduct ? selectedLoanProduct.productId : '';

        if (groupId && productId) {
          this.l360ApiService.getProductIdentificationTypes(groupId, productId, (response) => {
            if (response?.length > 0) {
              this.statefulService.wrap('mem:ola-identificationType-pick-list').value = JSON.stringify(response);
            }
          });
        }
      }
    }
  };

  public setValueFromSession = (context: LogicFnContext, propName: string, hideField: boolean): void => {
    if (!context?.field?.data) {
      return;
    }

    const rawValue = this.isBrowser ? sessionStorage.getItem(propName) : this.statefulService.wrap(propName).valueString;

    if (typeof rawValue === 'undefined' || rawValue === null || rawValue === '') {
      return;
    }

    const fieldType = context.field.data.config?.type;
    const parsedValue = fieldType === 'boolean' || fieldType === 'number' ? JSON.parse(rawValue) : rawValue;

    context.field.data.value = parsedValue;

    if (hideField) {
      context.field.config.visible = false;
    }
  };

  /**
   * Employment Status from Loan product
   * @param context Logic Fcn
   */
  public EmploymentStatusTypeEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const iterationID = getPageIterationId(context);
    const loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName', undefined, iterationID).value;
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName', undefined, iterationID).value;
    if (
      this.adminPortalFeaturesService.config.olaConfiguration &&
      this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups
    ) {
      const consumerLoanGroups = this.adminPortalFeaturesService.config.olaConfiguration.consumerLoanGroups;
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      if (selectedLoanGroup) {
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => t.productName === loanType || !selectedLoanGroup.isLoanGroupHasProduct
        )[0];
        if (selectedLoanProduct) {
          const pickList = selectedLoanProduct.employmentStatus;
          if (pickList) {
            const filteredPickList = pickList.filter((f) => f.isSelected === true);
            if (filteredPickList.length > 0 && filteredPickList != null) {
              const list = Array.from(new Set(filteredPickList)).map((item: any) => buildEnumItem(item.id, item.name));
              return list;
            }
          }
          return this.PicklistConfigAndL360EnumBuilder(context);
        }
      }
    }
  };

  /**
   * Preferred Branches for Loan Closing
   * @param context Logic Fcn
   */
  public closingPreferBranchEnumBuilder = (): ValueLabelItem[] => {
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    const branches = adminPortalFeatures?.preferredBranches;
    if (!branches) {
      return;
    }
    const picklists = branches.sort((n1, n2) => {
      if (n1.name > n2.name) {
        return 1;
      }

      if (n1.name < n2.name) {
        return -1;
      }

      return 0;
    });
    const list = Array.from(new Set(picklists)).map((item: any) => buildEnumItem(item.id, item.name));
    return list;
  };

  public returnModalTypeForCoappPages = (): string => {
    if (this.statefulService.wrap('ss:showApplicantRemoveModal').value) {
      return 'oao-confirm-delete-applicant';
    } else if (this.statefulService.wrap('ss:showMigratedApplicationModal').value) return 'migrated-application';
  };
}
